import {
  ArrowRed,
  Box,
  BrandPayoutRankingItem,
  Disclaimer,
  displayCentsInDollars,
  Div,
  getFinancesBrandRanking,
  getTimeZone,
  H5,
  TooltipIcon,
  Icon,
  lightTheme,
  ModalPortal,
  P,
  reportError,
} from '@vgrubs/components';
import { isAxiosError } from 'axios';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const SUMMARY_SIZE = 5;

const BrandPayoutRow = ({
  item,
  index,
}: {
  item: BrandPayoutRankingItem;
  index: number;
}) => {
  const navigate = useNavigate();
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    navigate(`/brand/${item.brandId}`);
  };
  const totalColor = item.total < 0 ? lightTheme.primary : lightTheme.green;

  // Ad spend is reported per provider (`adSpent` is UberEats). Each provider
  // is labelled and only listed when it actually has spend, so a brand that
  // only advertises on one of them still says which one it is.
  const adSpentUber = item.adSpent ?? 0;
  const adSpentGrubhub = item.adSpentGrubhub ?? 0;
  const adSpentLabel = [
    adSpentUber ? `UE ${displayCentsInDollars(adSpentUber)}` : null,
    adSpentGrubhub ? `GH ${displayCentsInDollars(adSpentGrubhub)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Div
      $flex
      $alignCenter
      $between
      $borderBottom
      $borderColor={lightTheme.lightGray}
      $height="70px"
      $heightSM="70px"
      $gap="12px"
      $pointer
      onClick={handleClick}
    >
      <Div>
        <P
          $bold
          $mb="4"
          $left
          $lineHeight="1.2"
          $pointer
          $hoverUnderline
          onClick={handleClick}
        >
          {index + 1}. {item.brandName}
        </P>
        <Disclaimer $m="0" $left>
          {item.locationName}
          {adSpentLabel && ` · Ad: ${adSpentLabel}`}
        </Disclaimer>
      </Div>
      <P $bold $m="0" $color={totalColor}>
        {displayCentsInDollars(item.total)}
      </P>
    </Div>
  );
};

/**
 * Brands ranked by summed provider "Total payout" over the range - every
 * provider that pays from a payment report (Uber, Grubhub) counts toward the
 * same figure. `sort="asc"` surfaces the worst (most negative) payouts first;
 * `sort="desc"` the best. The endpoint returns the full ranking (no
 * pagination), so we show the top 5 in the card and the whole list in a
 * scrollable modal.
 */
export const BrandPayoutList = ({
  from,
  to,
  sort,
  title,
  titleTooltip,
}: {
  from: string;
  to: string;
  sort: 'asc' | 'desc';
  title: string;
  /** Optional info-icon hover tooltip rendered next to the title. */
  titleTooltip?: string;
}) => {
  const [items, setItems] = useState<BrandPayoutRankingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const res = await getFinancesBrandRanking({
          sort,
          from: DateTime.fromISO(from).startOf('day').toISODate() || '',
          to: DateTime.fromISO(to).endOf('day').toISODate() || '',
          timeZone: getTimeZone,
        });
        if (!cancelled) setItems(res.data.data ?? []);
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response?.data.message !== 'Unauthorized'
        ) {
          reportError(error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [from, to, sort]);

  return (
    <>
      <Box
        $p="16px 20px"
        $relative
        $overflow="hidden"
        $height="100%"
        $flex
        $column
      >
        {items.length > 0 && (
          <Icon
            $pointer
            style={{ position: 'absolute', right: 16, top: 16 }}
            onClick={() => setIsOpen(true)}
            color={lightTheme.lightGray}
          >
            <ArrowRed />
          </Icon>
        )}
        <Div $flex $alignCenter $gap="6px" $mb="16">
          <H5 $m="0">{title}</H5>
          {titleTooltip && <TooltipIcon tooltip={titleTooltip} />}
        </Div>

        {/* Body grows to fill the card (cards stretch to the tallest sibling,
            taller on mobile's full-width layout) with a 5-row floor, so the
            empty placeholder centers vertically instead of pinning to the top. */}
        <Div $minHeight="350px" $flex $column style={{ flex: 1 }}>
          {items.slice(0, SUMMARY_SIZE).map((item, index) => (
            <BrandPayoutRow key={item.brandId} item={item} index={index} />
          ))}

          {items.length === 0 && !loading && (
            <Div $flex $center style={{ flex: 1 }}>
              <P $m="0">No data available.</P>
            </Div>
          )}
        </Div>
      </Box>

      <ModalPortal
        isOpen={isOpen}
        closeModal={() => setIsOpen(false)}
        backgroundColor={lightTheme.white}
        padding="24px"
        logo={false}
        width="500px"
        maxHeight="calc(65px * 10)"
        modalOverflow="auto"
        titleWithExit={title}
      >
        {items.map((item, index) => (
          <BrandPayoutRow key={item.brandId} item={item} index={index} />
        ))}
      </ModalPortal>
    </>
  );
};
